/**
 * Componentes do Guard reutilizáveis pelas páginas:
 *  - GuardPage (lista geral)
 *  - BannedsPage (modal ao clicar em banido)
 *  - AccountDetailPage (card Guard de uma conta)
 *
 * Tudo que envolve "ver dados do guard de uma conta + ban/unban duplo"
 * vem daqui pra manter UI consistente.
 */
import { useEffect, useState } from 'react';
import { Loader2, AlertTriangle, X, Hash, Ban } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getHwidDetail, getGuardByAccount, guardBan, guardUnban,
  type HwidDetail,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';

// ─────────────────────────────────────────────── primitives ─────────────

export function Modal({ children, onClose, maxW = '600px' }:
  { children: React.ReactNode; onClose: () => void; maxW?: string }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card rounded-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
           style={{ maxWidth: maxW }}>
        {children}
      </div>
    </div>
  );
}

export function GuardSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-black uppercase tracking-[0.15em] text-white/55 mb-2">{title}</p>
      {children}
    </div>
  );
}

export function GuardField({ label, value, mono }:
  { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-white/45 uppercase tracking-wider">{label}</p>
      <p className={`text-white/85 mt-0.5 ${mono ? 'font-mono text-[11px]' : 'text-xs'} break-all`}>
        {value}
      </p>
    </div>
  );
}

// ───────────────────────────────────────────────── account list ─────────

type AccountItem = {
  userNum: number;
  accountId?: string | null;
  ip?: string | null;
  lastLogin?: string | null;
  isOnline: boolean;
  charName?: string | null;
  activeBans: number;
};

export function GuardAccountList({ items, canBan, onBan, onUnban }: {
  items: AccountItem[];
  canBan: boolean;
  onBan: (userNum: number, accountId?: string | null) => void;
  onUnban: (userNum: number) => void;
}) {
  if (items.length === 0) {
    return <div className="text-xs text-white/40 py-3 text-center">Nenhuma conta</div>;
  }
  return (
    <div className="space-y-1.5">
      {items.map((a) => (
        <div key={a.userNum}
             className="flex items-center gap-3 text-xs rounded-lg px-3 py-2
                        bg-white/[0.02] border border-white/[0.04]">
          <span className={`w-2 h-2 rounded-full shrink-0 ${
            a.isOnline ? 'bg-emerald-400' : 'bg-white/15'
          }`} />
          <Link to={`/admin/account/${a.userNum}`}
                className="text-[#00e1d7] font-bold hover:underline min-w-[120px]">
            {a.accountId || `#${a.userNum}`}
          </Link>
          {a.isOnline && a.charName && (
            <span className="text-white/75 truncate">{a.charName}</span>
          )}
          <span className="text-white/45 font-mono ml-auto">{a.ip || '—'}</span>
          <span className="text-white/35 text-[10px]">
            {a.lastLogin ? new Date(a.lastLogin).toLocaleDateString('pt-BR') : '—'}
          </span>
          {a.activeBans > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-black bg-red-500/15
                             text-red-400 border border-red-500/30">
              {a.activeBans} BAN
            </span>
          )}
          {canBan && (
            <div className="flex gap-1">
              {a.activeBans > 0 ? (
                <button onClick={() => onUnban(a.userNum)}
                        className="px-2 py-1 rounded text-[10px] font-bold
                                   bg-emerald-500/10 text-emerald-400 border border-emerald-500/30
                                   hover:bg-emerald-500/20 cursor-pointer">
                  Desban
                </button>
              ) : (
                <button onClick={() => onBan(a.userNum, a.accountId)}
                        className="px-2 py-1 rounded text-[10px] font-bold
                                   bg-red-500/10 text-red-400 border border-red-500/30
                                   hover:bg-red-500/20 cursor-pointer">
                  Banir
                </button>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────── HWID detail (genérico) ────

/** Render do conteúdo do HwidDetail — usado dentro do modal OU embedded em card. */
export function GuardDetailContent({ data, canBan, onBan, onUnban }: {
  data: HwidDetail;
  canBan: boolean;
  onBan: (userNum: number, accountId?: string | null) => void;
  onUnban: (userNum: number) => void;
}) {
  return (
    <div className="space-y-5">
      {data.hwid && (
        <div className="grid grid-cols-3 gap-3 text-xs">
          <GuardField label="Último IP" value={data.lastSeenIp || '—'} mono />
          <GuardField label="Último Login"
                      value={data.lastLogin ? new Date(data.lastLogin).toLocaleString('pt-BR') : '—'} />
          <GuardField label="Último Heartbeat"
                      value={data.lastHeartbeatAt ? new Date(data.lastHeartbeatAt).toLocaleString('pt-BR') : '—'} />
        </div>
      )}

      {!data.hwid && (
        <div className="rounded-lg bg-amber-500/[0.05] border border-amber-500/20 p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/65 leading-relaxed">
            Esta conta nunca enviou heartbeat (sem HWID registrado). Mostrando só histórico de bans.
          </p>
        </div>
      )}

      {data.accountsSameHwid.length > 0 && (
        <GuardSection title={`Contas neste HWID (${data.accountsSameHwid.length})`}>
          <GuardAccountList items={data.accountsSameHwid} canBan={canBan}
                            onBan={onBan} onUnban={onUnban} />
        </GuardSection>
      )}

      {data.accountsSameIp.length > 0 && (
        <GuardSection title={`Contas no mesmo IP — HWID diferente (${data.accountsSameIp.length})`}>
          <GuardAccountList items={data.accountsSameIp} canBan={canBan}
                            onBan={onBan} onUnban={onUnban} />
        </GuardSection>
      )}

      <GuardSection title={`Histórico de bans (${data.bansHistory.length})`}>
        {data.bansHistory.length === 0 ? (
          <div className="text-xs text-white/40 py-3 text-center">Sem registros</div>
        ) : (
          <div className="space-y-2">
            {data.bansHistory.map((b) => (
              <div key={b.banId}
                   className={`text-xs rounded-lg p-3 border ${
                     b.isActive
                       ? 'bg-red-500/[0.06] border-red-500/30'
                       : 'bg-white/[0.02] border-white/[0.05] opacity-60'
                   }`}>
                <div className="flex items-center justify-between mb-1">
                  <Link to={`/admin/account/${b.userNum}`}
                        className="text-[#00e1d7] font-bold hover:underline">
                    UserNum {b.userNum}
                  </Link>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                    b.isActive ? 'bg-red-500/20 text-red-400' : 'bg-white/[0.05] text-white/45'
                  }`}>
                    {b.isActive ? 'ATIVO' : 'EXPIRADO'}
                  </span>
                </div>
                <p className="text-white/75 leading-relaxed">{b.motivo}</p>
                <p className="text-[10px] text-white/40 mt-1 font-mono">
                  banido em {new Date(b.dataBanimento).toLocaleString('pt-BR')}
                  {b.dataLiberacao && (
                    <> · libera em {new Date(b.dataLiberacao).toLocaleString('pt-BR')}</>
                  )}
                  {!b.dataLiberacao && b.isActive && <> · permanente</>}
                </p>
              </div>
            ))}
          </div>
        )}
      </GuardSection>
    </div>
  );
}

// ─────────────────────────────────────────── modais (por HWID e por user) ─

/** Modal completo do Guard. Aceita ENTRADA por HWID ou por UserNum.
 *  - Se `hwid` for passado: busca por HWID.
 *  - Se `userNum` for passado: resolve HWID atual da conta e busca, fallback bans-only. */
export function GuardDetailModal({ hwid, userNum, canBan, onClose, onBan, onChanged }: {
  hwid?: string | null;
  userNum?: number | null;
  canBan: boolean;
  onClose: () => void;
  onBan: (userNum: number, accountId?: string | null) => void;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<HwidDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { notify } = useToast();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = hwid
        ? await getHwidDetail(hwid)
        : userNum != null
          ? await getGuardByAccount(userNum)
          : null;
      if (!d) throw new Error('Sem alvo (hwid ou userNum)');
      setData(d);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hwid, userNum]);

  const onUnban = async (un: number) => {
    try {
      await guardUnban(un);
      notify('success', 'Desbanido');
      await fetchData();
      onChanged?.();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    }
  };

  const title = hwid ? 'HWID' : `Conta ${userNum}`;

  return (
    <Modal onClose={onClose} maxW="900px">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Hash size={18} className="text-amber-400" />
            {title}
          </h2>
          {hwid && <p className="text-[11px] font-mono text-white/55 mt-1 break-all">{hwid}</p>}
        </div>
        <button onClick={onClose} className="text-white/45 hover:text-white">
          <X size={18} />
        </button>
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : error ? (
        <div className="text-sm text-red-400 py-6 text-center">{error}</div>
      ) : data ? (
        <GuardDetailContent data={data} canBan={canBan} onBan={onBan} onUnban={onUnban} />
      ) : null}
    </Modal>
  );
}

// ────────────────────────────────────────────────── ban modal ───────────

export function GuardBanModal({ target, onClose, onDone }: {
  target: { userNum: number; accountId?: string | null };
  onClose: () => void;
  onDone: () => void;
}) {
  const [days, setDays] = useState(7);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const { notify } = useToast();

  const submit = async () => {
    if (motivo.trim().length < 2) {
      notify('error', 'Motivo obrigatório');
      return;
    }
    setBusy(true);
    try {
      await guardBan({ userNum: target.userNum, days, motivo: motivo.trim() });
      notify('success', `Banido por ${days} dias`);
      onDone();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} maxW="500px">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Ban size={18} className="text-red-400" />
          Banir conta + HWID
        </h2>
        <button onClick={onClose} className="text-white/45 hover:text-white">
          <X size={18} />
        </button>
      </div>

      <div className="space-y-4">
        <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 text-xs">
          <p className="text-white/55">Conta-alvo:</p>
          <p className="text-white font-bold mt-1">
            {target.accountId || `UserNum ${target.userNum}`}
            <span className="text-white/40 font-normal ml-2">#{target.userNum}</span>
          </p>
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/55 block mb-1.5">
            Duração (dias)
          </label>
          <input type="number" value={days} min={1} max={3650}
                 onChange={(e) => setDays(Math.max(1, Math.min(3650, +e.target.value || 1)))}
                 className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]
                            text-sm text-white outline-none focus:border-[#00e1d7]/40" />
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-white/55 block mb-1.5">
            Motivo
          </label>
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)}
                    rows={3} maxLength={512}
                    placeholder="Descreva o motivo do banimento..."
                    className="w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08]
                               text-sm text-white outline-none resize-none
                               focus:border-[#00e1d7]/40" />
        </div>

        <div className="rounded-lg bg-amber-500/[0.05] border border-amber-500/20 p-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-[11px] text-white/65 leading-relaxed">
            Banimento <b>duplo</b>: trava no login (Cabal nativo) E registra em
            <code className="text-white/85 mx-1">_39_AntiCheatBans</code>.
            Via match transitivo por HWID, todas as contas na mesma máquina serão bloqueadas pela DLL.
          </p>
        </div>

        <div className="flex gap-2 pt-2">
          <button onClick={onClose} disabled={busy}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold
                             bg-white/[0.04] border border-white/[0.08] text-white/75
                             hover:bg-white/[0.06] transition cursor-pointer">
            Cancelar
          </button>
          <button onClick={submit} disabled={busy || motivo.trim().length < 2}
                  className="flex-1 px-3 py-2 rounded-lg text-sm font-semibold
                             bg-red-500/15 border border-red-500/40 text-red-300
                             hover:bg-red-500/25 transition cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin inline" /> : 'Confirmar ban'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
